import { useState, useEffect, ReactNode, lazy, Suspense } from 'react';

// Notifications are only needed after the page has loaded.
const Toaster = lazy(() =>
  import('@/components/ui/toaster').then(mod => ({ default: mod.Toaster }))
);

interface DeferredProvidersProps {
  children: ReactNode;
}

/**
 * Children always render in the same place in the tree, so loading the
 * notification system later never re-creates the page (which would replay
 * animations and repaint the hero).
 */
const DeferredProviders = ({ children }: DeferredProvidersProps) => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(() => setIsReady(true), { timeout: 2000 });
      return () => cancelIdleCallback(id);
    }
    // Fallback for Safari
    const timer = setTimeout(() => setIsReady(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {children}
      {isReady && (
        <Suspense fallback={null}>
          <Toaster />
        </Suspense>
      )}
    </>
  );
};

export default DeferredProviders;
